import React, { useEffect, useMemo, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import queryString from 'query-string'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useAuth, useFeatureFlags } from '@node-space/hooks'
import { Loader, SmallTabs } from '@node-space/storybook-components'
import { Box } from '@node-space/ui-components'
import { WalletContextProvider } from 'contexts/walletContext'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useWalletQuery } from 'hooks/queries'
import { useBulkCapabiltiesQuery } from 'hooks/queries/Capabilities/useCapabiltiesQuery'
import { useIsActiveWallet } from 'hooks/useActiveWallets'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { ReceiveCrypto } from 'pages/Channels/ReceiveCrypto'
import { customerModelAtom } from 'pages/Customers/atoms/customersAtoms'
import { CustomerModels } from 'pages/Customers/helpers/types'
import { BulkCapabilityRequest } from 'types/capabilities/capabilities'
import { CustomerWalletDetails } from 'types/customers'
import { WalletType } from 'types/types'
import { getWalletActionCapabilityRequestFilters } from 'utils/capabilities'
import { getFiatWalletActionSupport } from 'utils/wallets/fiat'
import { ExtendedDetails } from './components/ExtendedDetails/ExtendedDetails'
import { RecentActivity } from './components/RecentActivity/RecentActivity'
import { WalletDetailsHeader } from './components/WalletDetailsHeader/WalletDetailsHeader'
import { WalletNotFound } from './components/WalletNotFound/WalletNotFound'

type TabQuery = 'recent-activity' | 'extended-details' | string

interface WalletDetailsProps {
  customerWalletDetails?: CustomerWalletDetails
}

export const WalletDetails = ({ customerWalletDetails }: WalletDetailsProps) => {
  const { t } = useTranslation()
  const {
    enableFiatCurrencyFilter,
    enableWalletDetailsTab,
    enableUsLaunchRestrictions,
    enableCustomerCryptoWalletTransactions,
  } = useFeatureFlags()
  const queryClient = useQueryClient()
  const {
    currentAccount,
    isLoading: isLoadingCurrentAccount,
    accountCapabilities,
  } = useAccountsContext()
  const { isEpcWalletPayinsCapabilityEnabled } = accountCapabilities || {}
  const { isAuthed } = useAuth()
  const { id }: { id?: string } = useParams()

  const customerModel = useAtomValue(customerModelAtom)
  const isEmbeddedCustomer = customerModel === CustomerModels.EMBEDDED
  const { tab } = queryString.parse(window.location.search) as { tab?: TabQuery }
  const [activeTab, setActiveTab] = useState(tab ?? 'recent-activity')

  const walletId = useMemo(
    () => customerWalletDetails?.legacyWalletId ?? id,
    [customerWalletDetails?.legacyWalletId, id]
  )

  const { data: walletData, isPending, isError } = useWalletQuery(Number(walletId), !!walletId)
  const permissions = useMappedRoles()?.wallets
  const isSelectedWalletActive = useIsActiveWallet(walletData)

  const fiatWalletCapabilitiesRequest: BulkCapabilityRequest = useMemo(() => {
    if (!!walletData?.lsid && currentAccount?.reference && isAuthed && !isLoadingCurrentAccount) {
      return getWalletActionCapabilityRequestFilters([walletData?.lsid])
    }
  }, [walletData, currentAccount?.reference, isAuthed, isLoadingCurrentAccount])

  const { data: bulkCapabilities, isFetching: isFetchingWalletCapabilities } =
    useBulkCapabiltiesQuery(
      fiatWalletCapabilitiesRequest,
      !!fiatWalletCapabilitiesRequest,
      'walletDetailsCapabilitiesCheck'
    )

  const wallet: WalletType = useMemo(() => {
    const walletProperties = {
      currency: walletData?.currency?.code,
      supportsWithdrawals: walletData?.supportsWithdrawals,
      supportsThirdParty: walletData?.supportsThirdParty,
      supportsDeposits: walletData?.supportsDeposits,
      lsid: walletData?.lsid,
      isLegacyWallet: !walletData?.supportedTransferDestinations?.length,
    }

    const { supportsPay, supportsDeposits, supportsWithdrawals, supportsInternalTransfers } =
      getFiatWalletActionSupport({
        walletProperties,
        bulkCapabilities,
        enableFiatCurrencyFilter,
      })

    return {
      ...walletData,
      supportsDeposits: supportsDeposits,
      supportsWithdrawals: supportsWithdrawals,
      supportsThirdParty: supportsPay,
      supportsInternalTransfers: supportsInternalTransfers,
    }
  }, [bulkCapabilities, walletData])

  const canEdit = permissions?.canEdit
  const canTrade = permissions?.canTrade
  const isFiatWallet = wallet?.currency?.fiat

  useEffect(() => {
    if (wallet?.supportsInternalBvnkNetworkTransfers && !!currentAccount?.reference) {
      queryClient.invalidateQueries({
        queryKey: [
          'bvnkNetworkConnections',
          {
            accountReference: currentAccount?.reference,
          },
        ],
      })
    }
  }, [wallet?.supportsInternalBvnkNetworkTransfers, currentAccount?.reference])

  const switchToTab = (tab: string) => {
    // add query param to current URL without causing page reload
    window.history.pushState({}, '', `?tab=${tab}`)
    setActiveTab(tab)
  }

  const showWalletDetailsHeader = (canEdit || canTrade) && !customerWalletDetails
  const isExtendedWalletDetailsAvailable =
    (isFiatWallet && wallet?.supportsDeposits) || (!isFiatWallet && !wallet?.supportsThirdParty)

  const isWalletDetailsTabVisible = useMemo(() => {
    const isVisible =
      isExtendedWalletDetailsAvailable &&
      isSelectedWalletActive &&
      (isFiatWallet || (enableWalletDetailsTab && !isFiatWallet))

    if (customerWalletDetails && isEmbeddedCustomer) {
      return isEmbeddedCustomer && isEpcWalletPayinsCapabilityEnabled
    }

    return isVisible
  }, [
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    isFiatWallet,
    enableWalletDetailsTab,
    isEmbeddedCustomer,
    customerWalletDetails,
    isEpcWalletPayinsCapabilityEnabled,
  ])

  const isReceiveCryptoTabVisible = useMemo(() => {
    const isVisible =
      isExtendedWalletDetailsAvailable && isSelectedWalletActive && !enableUsLaunchRestrictions

    if (customerWalletDetails && isEmbeddedCustomer) {
      return (
        isVisible && enableCustomerCryptoWalletTransactions && isEpcWalletPayinsCapabilityEnabled
      )
    }

    return isVisible
  }, [
    customerWalletDetails,
    isExtendedWalletDetailsAvailable,
    isSelectedWalletActive,
    enableUsLaunchRestrictions,
    isEmbeddedCustomer,
    isEpcWalletPayinsCapabilityEnabled,
  ])

  const tabs = [
    {
      id: 'recent-activity',
      tabHeader: t('wallets.transactionHistory'),
      tabContent: <RecentActivity walletId={wallet?.id} />,
      onClick: () => switchToTab('recent-activity'),
      testid: 'wallet-details-transaction-history-tab',
    },
  ]

  if (isWalletDetailsTabVisible) {
    const walletDetailsTab = {
      id: 'extended-details',
      tabHeader: t('walletDetails'),
      tabContent: <ExtendedDetails />,
      onClick: () => switchToTab('extended-details'),
      testid: 'wallet-details-extended-details-tab',
    }
    tabs.push(walletDetailsTab)
  }

  if (isReceiveCryptoTabVisible) {
    const receiveCryptoTab = {
      id: 'receive-crypto',
      tabHeader: t('receiveCrypto'),
      tabContent: <ReceiveCrypto />,
      onClick: () => switchToTab('receive-crypto'),
      testid: 'wallet-details-receive-crypto-tab',
    }
    tabs.push(receiveCryptoTab)
  }

  return (
    <Box>
      {isError && <WalletNotFound />}

      {(isPending || isFetchingWalletCapabilities) && (
        <Box className="flex justify-center">
          <Loader theme="dark" size="large" />
        </Box>
      )}

      {!isError && !isPending && !isFetchingWalletCapabilities && (
        <WalletContextProvider wallet={wallet}>
          {showWalletDetailsHeader && <WalletDetailsHeader />}
          {isExtendedWalletDetailsAvailable ? (
            <SmallTabs tabs={tabs} currentValueId={activeTab} />
          ) : (
            <RecentActivity walletId={wallet?.id} customerWallet={customerWalletDetails} />
          )}
        </WalletContextProvider>
      )}
    </Box>
  )
}
